// NodeUrlModal.jsx

import React, { useEffect, useState, useCallback } from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import { isUrlInputValid } from 'utilities/Url';
import {
  buildUrlFieldValueFromInput,
  getUrlString,
  normalizeUrlFieldValue,
} from 'utilities/UrlFieldValue';

// CSS
import './NodeUrlModal.css';

// Components
import Button from 'components/button/Button';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalForm from 'components/modal/form/ModalForm';
import Modal from 'components/modal/Modal';
import TextInput from 'components/textinput/TextInput';

/**
 * Modal for editing a schema-driven url field (structured value).
 */
const NodeUrlModal = ({ label, value, isOpen, setOpen, onChange }) => {
  const isMobile = useIsMobile();
  const [url, setUrl] = useState('');
  const [indexContent, setIndexContent] = useState(false);
  const [isValid, setIsValid] = useState(true);

  const computeValid = useCallback((nextValue) => isUrlInputValid(nextValue), []);

  useEffect(() => {
    if (!isOpen) return;
    const normalized = normalizeUrlFieldValue(value);
    const initial = normalized?.url || getUrlString(value) || '';
    setUrl(initial);
    setIndexContent(normalized?.indexContent === true);
    setIsValid(computeValid(initial));
  }, [isOpen, value, computeValid]);

  const handleSave = () => {
    if (!isValid) return;
    const trimmed = (url || '').trim();
    if (!trimmed) {
      onChange(null);
      setOpen(false);
      return;
    }
    const next = buildUrlFieldValueFromInput(trimmed, value);
    onChange({
      ...next,
      indexContent,
    });
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && isValid) {
      e.preventDefault();
      handleSave();
    }
  };

  return (
    <Modal
      title={label || 'Edit'}
      isOpen={isOpen}
      height={320}
      width={isMobile ? '100%' : 460}
      onClose={() => setOpen(false)}
    >
      <ModalForm>
        <div className="node-url-modal-wrapper">
          <TextInput
            value={url}
            maxLength={2048}
            autoFocus
            showCount
            placeholder="https://example.com/path (leave empty to clear)"
            onChange={(e) => {
              const next = e.target.value;
              setUrl(next);
              setIsValid(computeValid(next));
            }}
            onKeyDown={handleKeyDown}
          />

          <label className="node-url-index-toggle">
            <input
              type="checkbox"
              checked={indexContent}
              onChange={(e) => setIndexContent(e.target.checked)}
            />
            <span>
              Index linked page for Ask
              <span className="node-url-index-hint">
                Fetches readable page text into knowledge (Knowledge module is visitor-visible in Chat).
              </span>
            </span>
          </label>
        </div>
      </ModalForm>
      <ModalButtons>
        <Button
          label="Save"
          enabled={isValid}
          onClick={handleSave}
        />
      </ModalButtons>
    </Modal>
  );
};

NodeUrlModal.displayName = 'NodeUrlModal';

export default NodeUrlModal;
